<script>
    import { selectedItem } from "../../../store/store.js";
    import {
        displayInt,
        tooltip,
        calculateNettoskuld,
        safeDivide,
        safeMultiply,
    } from "../../../utils/utils.js";

    function displayAnnualFeeIncome(itemData) {
        if (itemData.annual_fee_income != null) {
            return displayInt(itemData.annual_fee_income);
        } else {
            return displayInt(
                Math.round(
                    itemData.living_fee_per_sqm * itemData.living_area_sqm,
                ),
            );
        }
    }

    function calculateCommercialIncomePerSqm(itemData) {
        if (
            itemData.commercial_area_sqm &&
            itemData.commercial_area_sqm > 0 &&
            itemData.rental_income_from_commercial_premises != null
        ) {
            return displayInt(
                Math.round(
                    itemData.rental_income_from_commercial_premises /
                        itemData.commercial_area_sqm,
                ),
            );
        }
    }

    function displayFeePerSqm(itemData) {
        const { living_fee_per_sqm, annual_fee_income, living_area_sqm } =
            itemData;
        if (living_fee_per_sqm <= 2000) {
            return displayInt(living_fee_per_sqm);
        }

        if (
            annual_fee_income != null &&
            living_area_sqm != null &&
            living_area_sqm > 0
        ) {
            const fee = Math.round(annual_fee_income / living_area_sqm);

            if (fee > 2000) {
                return displayInt(living_fee_per_sqm);
            }

            return displayInt(fee);
        }

        return displayInt(living_fee_per_sqm);
    }

    function toTitleCase(str) {
        if (!str) return "Uppgifter saknas"; // Handle missing data
        return str
            .toLowerCase()
            .split(" ")
            .map((word) => {
                // Handle potential empty strings after split
                if (word.length === 0) return "";
                return word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join(" ");
    }

    function calculateRorelseresultatPerSqm(itemData) {
        if (
            itemData.ebit != null &&
            itemData.living_area_sqm != null &&
            itemData.living_area_sqm > 0
        ) {
            return displayInt(
                Math.round(itemData.ebit / itemData.living_area_sqm),
            );
        }
    }

    function calculateLoansSumDifference(itemData) {
        if (
            !itemData.loans ||
            !Array.isArray(itemData.loans) ||
            itemData.loans.length === 0
        ) {
            return {
                hasSignificantDifference: false,
                difference: 0,
                calculatedSum: 0,
            };
        }

        const calculatedSum = itemData.loans.reduce((sum, loan) => {
            return sum + (loan.amount || 0);
        }, 0);

        const totalAmount = itemData.total_amount_of_loans || 0;
        const difference = Math.abs(totalAmount - calculatedSum);

        // Only show warning if difference is more than 100 SEK (a couple of SEK)
        const hasSignificantDifference = difference > 100;

        return {
            hasSignificantDifference,
            difference,
            calculatedSum,
        };
    }

    function calculateLoansPerSqmDifference(itemData) {
        if (
            !itemData.loans_per_living_sqm ||
            !itemData.total_amount_of_loans ||
            !itemData.living_area_sqm ||
            itemData.living_area_sqm === 0
        ) {
            return {
                hasSignificantDifference: false,
                percentageDifference: 0,
                calculatedValue: 0,
                reportedValue: 0,
            };
        }

        const calculatedValue = itemData.total_amount_of_loans / itemData.living_area_sqm;
        const reportedValue = itemData.loans_per_living_sqm;
        const difference = Math.abs(calculatedValue - reportedValue);
        const percentageDifference = (difference / reportedValue) * 100;

        const hasSignificantDifference = percentageDifference > 1;

        return {
            hasSignificantDifference,
            percentageDifference,
            calculatedValue,
            reportedValue,
        };
    }

    let loanDifference = {
        hasSignificantDifference: false,
        difference: 0,
        calculatedSum: 0,
    };

    let loansPerSqmDifference = {
        hasSignificantDifference: false,
        percentageDifference: 0,
        calculatedValue: 0,
        reportedValue: 0,
    };

    $: loanDifference = calculateLoansSumDifference($selectedItem);
    $: loansPerSqmDifference = calculateLoansPerSqmDifference($selectedItem);
</script>

<div class="key-value-table box">
    <div class="header-card">
        <h5>
            <i class="fa-solid fa-chart-line fa-sm icon-detail"></i> Ekonomi & Intäkter
        </h5>
    </div>

    <div class="sections-container">
        <!-- Income Section -->
        <div class="section">
            <h6 class="section-title">Intäkter</h6>
            <div class="metrics-grid">
                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Årlig avgiftsinkomst</h6>
                    </div>
                    <div class="metric-value">
                        {displayAnnualFeeIncome($selectedItem)} kr
                    </div>
                    {#if $selectedItem.living_area_sqm}
                        <div class="metric-subvalue">
                            {safeDivide(
                                displayAnnualFeeIncome($selectedItem),
                                $selectedItem.living_area_sqm,
                                "kr/m²",
                            )}
                        </div>
                    {/if}
                </div>

                {#if $selectedItem.rental_income_from_commercial_premises}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Lokalintäkter</h6>
                            <div class="tooltip-container" use:tooltip>
                                <span class="info-icon"></span>
                                <div class="tooltip-content">
                                    <p>
                                        Lokalintäkter avser de inkomster en
                                        bostadsrättsförening erhåller från
                                        uthyrning av kommersiella ytor såsom
                                        butiker, kontor eller andra
                                        verksamhetslokaler. Dessa intäkter kan
                                        ha en betydande inverkan på föreningens
                                        ekonomi och påverka medlemmarnas
                                        avgifter.
                                    </p>
                                    <p>
                                        <strong
                                            >Betydelsen av lokalintäkter:</strong
                                        >
                                    </p>
                                    <ul>
                                        <li>
                                            Ekonomisk stabilitet: Stabila och
                                            långsiktiga hyresavtal med
                                            kommersiella aktörer kan ge
                                            föreningen en förutsägbar och
                                            pålitlig inkomstkälla, vilket bidrar
                                            till ekonomisk stabilitet.
                                        </li>
                                        <li>
                                            Subventionering av medlemsavgifter:
                                            Höga lokalintäkter kan möjliggöra
                                            lägre månadsavgifter för
                                            medlemmarna, eftersom dessa intäkter
                                            kan täcka en del av föreningens
                                            drift- och underhållskostnader.
                                        </li>
                                        <li>
                                            Riskhantering: Det är viktigt att
                                            beakta risken för vakanser. Om en
                                            lokal står tom under en längre
                                            period kan det påverka föreningens
                                            ekonomi negativt.
                                        </li>
                                    </ul>
                                    {#if $selectedItem.commercial_area_sqm}
                                        <p>
                                            <strong
                                                >Lokalintäkter per kvadratmeter:</strong
                                            >
                                        </p>
                                        <p>
                                            När lokalintäkter anges per
                                            kvadratmeter (kr/m²) ger det en bild
                                            av hur mycket föreningen tjänar på
                                            varje kvadratmeter uthyrd lokalyta.
                                            Detta nyckeltal är användbart för
                                            att jämföra intäktsnivån mellan
                                            olika lokaler eller med
                                            branschstandarder.
                                        </p>
                                    {/if}
                                </div>
                            </div>
                        </div>
                        <div class="metric-value">
                            {displayInt(
                                $selectedItem.rental_income_from_commercial_premises,
                            )} kr
                        </div>
                        {#if $selectedItem.commercial_area_sqm && $selectedItem.commercial_area_sqm > 0 && $selectedItem.rental_income_from_commercial_premises != null}
                            <div class="metric-subvalue">
                                {calculateCommercialIncomePerSqm($selectedItem)}
                                kr/m²
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if $selectedItem.rental_income_from_rental_apartments}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Hyresintäkter från uthyrningslägenheter</h6>
                            <div class="tooltip-container" use:tooltip>
                                <span class="info-icon"></span>
                                <div class="tooltip-content">
                                    <p>
                                        Hyresintäkter från uthyrningslägenheter avser
                                        inkomster som föreningen får från lägenheter
                                        som föreningen själv äger och hyr ut till
                                        hyresgäster.
                                    </p>
                                    <p>
                                        Dessa intäkter kan bidra till föreningens
                                        ekonomi och potentiellt hålla nere
                                        medlemsavgifterna.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.rental_income_from_rental_apartments)} kr
                        </div>
                        {#if $selectedItem.rental_apartments_sqm}
                            <div class="metric-subvalue">
                                {safeDivide(
                                    displayInt($selectedItem.rental_income_from_rental_apartments),
                                    $selectedItem.rental_apartments_sqm,
                                    "kr/m²",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if $selectedItem.other_revenues}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Andra intäkter</h6>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.other_revenues)} kr
                        </div>
                        {#if $selectedItem.living_area_sqm}
                            <div class="metric-subvalue">
                                {safeDivide(
                                    displayInt($selectedItem.other_revenues),
                                    $selectedItem.living_area_sqm,
                                    "kr/m²",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}

                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Avgift per kvm</h6>
                        <div class="tooltip-container" use:tooltip>
                            <span class="info-icon"></span>
                            <div class="tooltip-content">
                                <p>
                                    Avgift per kvadratmeter (avgift/kvm) är ett
                                    viktigt nyckeltal som visar hur mycket en
                                    bostadsrättsinnehavare betalar i
                                    månadsavgift i förhållande till boytan.
                                    Detta nyckeltal ger en indikation på
                                    föreningens löpande kostnader och ekonomiska
                                    hälsa. Det finns ingen exakt regel för vad
                                    som är en "bra" avgift per kvm, men här är
                                    några allmänna riktlinjer:
                                </p>
                                <p>
                                    <strong
                                        >Sammanfattning av vad man vill se:</strong
                                    >
                                </p>
                                <ul>
                                    <li>
                                        Låg avgift per kvm (under 600 SEK/kvm):
                                        Indikerar ofta en välskött förening med
                                        låga driftskostnader eller andra
                                        inkomstkällor som bidrar till ekonomin.
                                    </li>
                                    <li>
                                        Måttlig avgift per kvm (600–900
                                        SEK/kvm): Anses vara en normal nivå för
                                        många föreningar och speglar ofta en
                                        balanserad ekonomi med tillräckliga
                                        medel för underhåll och drift.
                                    </li>
                                    <li>
                                        Hög avgift per kvm (över 900 SEK/kvm):
                                        Kan signalera högre driftskostnader,
                                        större lån eller planerade renoveringar,
                                        vilket kan innebära en högre ekonomisk
                                        belastning för medlemmarna.
                                    </li>
                                </ul>
                                <p><strong>Detaljerade riktlinjer:</strong></p>
                                <p>
                                    Det är viktigt att notera att avgiften per
                                    kvm kan variera beroende på faktorer som
                                    fastighetens ålder, skick, föreningens
                                    skuldsättning och eventuella kommersiella
                                    intäkter. Föreningar med intäkter från
                                    lokaler eller andra kommersiella ytor kan
                                    ofta hålla nere avgifterna för medlemmarna.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="metric-value">
                        {displayFeePerSqm($selectedItem)} kr/m²
                    </div>
                </div>
            </div>
        </div>

        <!-- Financial Results Section -->
        <div class="section">
            <h6 class="section-title">Ekonomiska Resultat</h6>
            <div class="metrics-grid">
                {#if $selectedItem.ebit}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Rörelseresultat</h6>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.ebit)} kr
                        </div>
                        {#if $selectedItem.commercial_area_sqm && $selectedItem.commercial_area_sqm > 0 && $selectedItem.ebit != null}
                            <div class="metric-subvalue">
                                {calculateRorelseresultatPerSqm($selectedItem)} kr/m²
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if $selectedItem.profit_after_financial_items}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Resultat efter finansiella poster</h6>
                        </div>
                        <div class="metric-value">
                            {displayInt(
                                $selectedItem.profit_after_financial_items,
                            )}
                            kr
                        </div>
                        {#if $selectedItem.living_area_sqm}
                            <div class="metric-subvalue">
                                {safeDivide(
                                    $selectedItem.profit_after_financial_items,
                                    $selectedItem.living_area_sqm,
                                    "kr/m²",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if $selectedItem.solidity}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Soliditet</h6>
                            <div class="tooltip-container" use:tooltip>
                                <span class="info-icon"></span>
                                <div class="tooltip-content">
                                    <p>
                                        Visar hur stor andel av föreningens
                                        totala tillgångar som finansieras med
                                        eget kapital. Ett mått på finansiell
                                        stabilitet.
                                    </p>
                                    <!-- Add more detailed text from Economy.svelte info box if needed -->
                                </div>
                            </div>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.equity_ratio)} %
                        </div>
                    </div>
                {/if}

                {#if $selectedItem.saving_per_sqm}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Sparande per kvm</h6>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.saving_per_sqm)} kr/m²
                        </div>
                        {#if $selectedItem.living_area_sqm}
                            <div class="metric-subvalue">
                                {safeMultiply(
                                    displayInt($selectedItem.saving_per_sqm),
                                    $selectedItem.living_area_sqm,
                                    "kr",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>
        </div>
    </div>

    <div class="sections-container">
        <!-- Assets & Liquidity Section -->
        <div class="section">
            <h6 class="section-title">Tillgångar & Likviditet</h6>
            <div class="metrics-grid">
                {#if $selectedItem.kassa_och_bank}
                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Kassa och bank</h6>
                    </div>
                    <div class="metric-value">
                        {displayInt($selectedItem.kassa_och_bank)} kr
                    </div>
                    {#if $selectedItem.living_area_sqm}
                        <div class="metric-subvalue">
                            {safeDivide(
                                displayInt($selectedItem.kassa_och_bank),
                                $selectedItem.living_area_sqm,
                                "kr/m²",
                            )}
                        </div>
                    {/if}
                </div>
                {/if}
                {#if $selectedItem.cash_at_the_end_of_the_year}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Likvida medel vid årets slut</h6>
                        </div>
                        <div class="metric-value">
                            {displayInt(
                                $selectedItem.cash_at_the_end_of_the_year,
                            )} kr
                        </div>
                        {#if $selectedItem.living_area_sqm}
                            <div class="metric-subvalue">
                                {safeDivide(
                                    displayInt(
                                        $selectedItem.cash_at_the_end_of_the_year,
                                    ),
                                    $selectedItem.living_area_sqm,
                                    "kr/m²",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}
                {#if $selectedItem.skattekonto}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Skattekonto</h6>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.skattekonto)} kr
                        </div>
                        {#if $selectedItem.living_area_sqm}
                            <div class="metric-subvalue">
                                {safeDivide(
                                    displayInt($selectedItem.skattekonto),
                                    $selectedItem.living_area_sqm,
                                    "kr/m²",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if $selectedItem.klientmedel}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Klientmedel</h6>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.klientmedel)} kr
                        </div>
                        {#if $selectedItem.living_area_sqm}
                            <div class="metric-subvalue">
                                {safeDivide(
                                    displayInt($selectedItem.klientmedel),
                                    $selectedItem.living_area_sqm,
                                    "kr/m²",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if $selectedItem.other_receivables}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Övriga fordringar</h6>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.other_receivables)} kr
                        </div>
                        {#if $selectedItem.living_area_sqm}
                            <div class="metric-subvalue">
                                {safeDivide(
                                    displayInt($selectedItem.other_receivables),
                                    $selectedItem.living_area_sqm,
                                    "kr/m²",
                                )}
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>
        </div>

        <!-- Loans Section -->
        <div class="section">
            <h6 class="section-title">Lån & Skuldsättning</h6>
            <div class="metrics-grid">
                {#if $selectedItem.loans_per_living_sqm}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Lån per bostadsyta</h6>
                            <div class="tooltip-container">
                                <span class="info-icon"></span>
                                <div class="tooltip-content">
                                    <p>
                                        Denna siffra baseras på föreningens
                                        totala lån och den totala boytan.
                                    </p>
                                    <p>
                                        Det finns ingen exakt regel för vad som
                                        är ett "bra" lån per kvm, men här är
                                        några allmänna riktlinjer:
                                    </p>
                                    <p>
                                        <strong
                                            >Sammanfattning av vad man vill se:</strong
                                        >
                                    </p>
                                    <ul>
                                        <li>
                                            Låga lån per kvm (under 5 000
                                            SEK/kvm) är mest eftertraktat för
                                            stabilitet och långsiktig trygghet.
                                        </li>
                                        <li>
                                            Måttliga lån per kvm (5 000 - 10 000
                                            SEK/kvm) kan anses rimliga beroende
                                            på föreningens planerade underhåll
                                            och ekonomiska plan.
                                        </li>
                                        <li>
                                            Höga lån per kvm (över 10 000
                                            SEK/kvm) ökar risken för framtida
                                            avgiftshöjningar och kan signalera
                                            en tung skuldbörda, vilket kräver en
                                            närmare analys av föreningens
                                            ekonomiska plan och strategi.
                                        </li>
                                    </ul>
                                    <p>
                                        <strong>Detaljerade riktlinjer:</strong>
                                    </p>
                                    <p>
                                        Det är vanligt att nyare föreningar ofta
                                        har lite högre skuldsättning.
                                    </p>
                                    <p>
                                        Om föreningen har intäkter från lokaler,
                                        kontor eller andra kommersiella ytor kan
                                        en högre skuldsättningsgrad vara mer
                                        acceptabel. Detta beror på att dessa
                                        ytor inte inkluderas i beräkningen av
                                        lån per kvadratmeter, och intäkterna kan
                                        bidra till att täcka föreningens
                                        kostnader och minska risken för
                                        avgiftshöjningar.
                                    </p>

                                    <ul>
                                        <li>
                                            <strong
                                                >Lågt lån per kvm (0 – 5 000
                                                SEK/kvm):</strong
                                            >
                                        </li>
                                        <ul>
                                            <li>
                                                Föreningar med låga lån har ofta
                                                en sund ekonomi och större
                                                utrymme för att möta framtida
                                                underhåll och räntehöjningar
                                                utan att behöva höja avgifterna
                                                dramatiskt. Det kan vara ett
                                                tecken på att föreningen har
                                                betalat av sina lån eller har
                                                finansierat större underhåll
                                                utan att ta på sig mycket skuld.
                                            </li>
                                            <li>
                                                <strong>Fördel:</strong> Stabilare
                                                ekonomi, lägre risk för avgiftshöjningar.
                                            </li>
                                            <li>
                                                <strong>Nackdel:</strong> Kan signalera
                                                att avgifterna är för höga eller
                                                att föreningen inte har investerat
                                                tillräckligt i fastigheten.
                                            </li>
                                        </ul>

                                        <li>
                                            <strong
                                                >Måttligt lån per kvm (5 000 –
                                                10 000 SEK/kvm):</strong
                                            >
                                        </li>
                                        <ul>
                                            <li>
                                                Detta anses ofta som en sund
                                                nivå för föreningar som gör
                                                kontinuerliga renoveringar och
                                                underhåll. Lån i denna nivå kan
                                                indikera att föreningen har
                                                tagit lån för större
                                                renoveringar (t.ex. stambyten
                                                eller fasadrenoveringar), men
                                                att skuldbördan fortfarande är
                                                hanterbar.
                                            </li>
                                            <li>
                                                <strong>Fördel:</strong> Bra balans
                                                mellan investeringar och skuldbörda.
                                            </li>
                                            <li>
                                                <strong>Nackdel:</strong> Liten risk
                                                för framtida avgiftshöjningar beroende
                                                på ränteutvecklingen och nya underhållsbehov.
                                            </li>
                                        </ul>

                                        <li>
                                            <strong
                                                >Högt lån per kvm (över 10 000
                                                SEK/kvm):</strong
                                            >
                                        </li>
                                        <ul>
                                            <li>
                                                Om lån per kvm överstiger 10 000
                                                SEK kan det vara en
                                                varningssignal. Det indikerar
                                                att föreningen har en hög
                                                skuldbörda i förhållande till
                                                sin boyta, vilket ökar risken
                                                för framtida avgiftshöjningar,
                                                speciellt vid räntehöjningar
                                                eller oförutsedda utgifter.
                                            </li>
                                            <li>
                                                <strong>Fördel:</strong> Föreningen
                                                kan ha finansierat viktiga renoveringar.
                                            </li>
                                            <li>
                                                <strong>Nackdel:</strong> Stor risk
                                                för höga avgiftshöjningar om föreningen
                                                inte hanterar sin skuldbörda och
                                                framtida underhåll väl.
                                            </li>
                                        </ul>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.loans_per_living_sqm)} kr/m²
                        </div>
                        {#if loansPerSqmDifference.hasSignificantDifference}
                            <div class="tooltip-container warning-bottom-right" use:tooltip>
                                <span class="warning-indicator">!</span>
                                <div class="tooltip-content">
                                    <p>
                                        <strong>Lån per kvm stämmer inte överens!</strong>
                                    </p>
                                    <p>
                                        Det rapporterade värdet ({displayInt(
                                            loansPerSqmDifference.reportedValue,
                                        )} kr/m²) skiljer sig från det beräknade
                                        värdet baserat på total lånesumma och boyta
                                        ({displayInt(
                                            loansPerSqmDifference.calculatedValue,
                                        )} kr/m²).
                                    </p>
                                    <p>
                                        <strong
                                            >Differens: {loansPerSqmDifference.percentageDifference.toFixed(
                                                1,
                                            )}%</strong
                                        >
                                    </p>
                                    <p>
                                        {#if $selectedItem.rental_income_from_rental_apartments && !$selectedItem.rental_apartments_sqm}
                                        <p>
                                            Denna skillnad kan bero på att föreningen har uthyrningslägenheter men att
                                            ytan för dessa inte är angiven. Lån per kvm under den totala summan lån
                                            baseras troligtvis på total bostadsyta (inklusive uthyrningslägenheter). 
                                        </p>
                                        <p>
                                            För att få en mer exakt bild av lån per kvm bör ytan för uthyrningslägenheter också anges i årsredovisningen.
                                        </p>
                                        {:else}
                                        Detta kan tyda på felaktiga siffror i
                                        årsredovisningen. Kontrollera att lån
                                        per kvm och total lånesumma är korrekt
                                        rapporterade.
                                        {/if}
                                    </p>
                                </div>
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if $selectedItem.loans_per_total_sqm && $selectedItem.loans_per_total_sqm !== $selectedItem.loans_per_living_sqm}
                    <div class="metric-card">
                        <div class="metric-header">
                            <h6>Lån per total yta</h6>
                            <div class="tooltip-container">
                                <span class="info-icon"></span>
                                <div class="tooltip-content">
                                    <p>
                                        Visar föreningens totala lån dividerat
                                        med den totala ytan.
                                    </p>
                                    <p>
                                        Denna siffra är rapporterad av
                                        föreningen.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="metric-value">
                            {displayInt($selectedItem.loans_per_total_sqm)} kr/m²
                        </div>
                    </div>
                {/if}

                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Total summa lån</h6>
                        {#if loanDifference.hasSignificantDifference}
                            <div class="tooltip-container" use:tooltip>
                                <span class="warning-indicator">!</span>
                                <div class="tooltip-content">
                                    <p>
                                        <strong
                                            >Lånesummorna stämmer inte överens!</strong
                                        >
                                    </p>
                                    <p>
                                        Föreningens totala lånesumma ({displayInt(
                                            $selectedItem.total_amount_of_loans,
                                        )} kr) skiljer sig från summan av de enskilda
                                        lånen ({displayInt(
                                            loanDifference.calculatedSum,
                                        )} kr).
                                    </p>
                                    <p>
                                        <strong
                                            >Differens: {displayInt(
                                                loanDifference.difference,
                                            )} kr</strong
                                        >
                                    </p>
                                    <p>
                                        Detta kan tyda på felaktiga siffror i
                                        årsredovisningen, vilket är en allvarlig
                                        varningssignal för föreningens ekonomi.
                                        Kontrollera årsredovisningen noggrant.
                                    </p>
                                </div>
                            </div>
                        {/if}
                    </div>
                    <div class="metric-value">
                        {displayInt($selectedItem.total_amount_of_loans)} kr
                    </div>
                    {#if $selectedItem.living_area_sqm}
                        <div class="metric-subvalue">
                            {safeDivide(
                                displayInt($selectedItem.total_amount_of_loans),
                                $selectedItem.living_area_sqm,
                                "kr/m²",
                            )}
                        </div>
                    {/if}
                </div>

                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Räntekänslighet</h6>
                        <div class="tooltip-container" use:tooltip>
                            <span class="info-icon"></span>
                            <div class="tooltip-content">
                                <p>
                                    Visar hur mycket årsavgiften (i procent)
                                    behöver höjas om den genomsnittliga räntan
                                    på föreningens lån ökar med 1 procentenhet.
                                </p>
                                <p>Hög räntekänslighet kan leda till:</p>
                                <ul>
                                    <li>
                                        Kraftiga avgiftshöjningar för medlemmar.
                                    </li>
                                    <li>
                                        Svårigheter att refinansiera lån (banker
                                        kan kräva högre räntor eller
                                        säkerheter).
                                    </li>
                                    <li>
                                        Minskad attraktivitet på marknaden
                                        (lägre efterfrågan på lägenheter).
                                    </li>
                                </ul>
                                <p>Låg räntekänslighet innebär:</p>
                                <ul>
                                    <li>
                                        Stabila avgifter även vid ränteschock.
                                    </li>
                                    <li>
                                        Förtroende från banker och potentiella
                                        köpare.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="metric-value">
                        {$selectedItem.rate_sensitivity != null
                            ? $selectedItem.rate_sensitivity + " %"
                            : "Uppgifter saknas"}
                    </div>
                </div>

                <div class="metric-card">
                    <div class="metric-header">
                        <h6>Indirekt Nettoskuldsättning</h6>
                        <div class="tooltip-container" use:tooltip>
                            <span class="info-icon"></span>
                            <div class="tooltip-content">
                                <p>
                                    Föreningens räntebärande skulder minus
                                    räntebärande tillgångar och likvida medel.
                                    Ger en bild av den faktiska skulden.
                                </p>
                                <!-- Add more detailed text from Economy.svelte info box if needed -->
                            </div>
                        </div>
                    </div>
                    <div class="metric-value">
                        {displayInt(
                            calculateNettoskuld(
                                $selectedItem.total_amount_of_loans,
                                $selectedItem.cash_at_the_end_of_the_year,
                                $selectedItem.skattekonto,
                                $selectedItem.klientmedel,
                                $selectedItem.kassa_och_bank,
                            ),
                        )} kr
                    </div>
                    {#if $selectedItem.living_area_sqm}
                        <div class="metric-subvalue">
                            {safeDivide(
                                calculateNettoskuld(
                                    $selectedItem.total_amount_of_loans,
                                    $selectedItem.cash_at_the_end_of_the_year,
                                    $selectedItem.skattekonto,
                                    $selectedItem.klientmedel,
                                    $selectedItem.kassa_och_bank,
                                ),
                                $selectedItem.living_area_sqm,
                                "kr/m²",
                            )}
                        </div>
                    {/if}
                </div>
            </div>
        </div>
    </div>

    <!-- Loans Details Section -->
    {#if $selectedItem?.loans?.length > 0}
        <div class="section loans-detail-section">
            <h6 class="section-title">Låneinformation</h6>
            <div class="loan-table-container">
                <table class="loan-table">
                    <thead>
                        <tr>
                            <th class="bank-column">Bank</th>
                            <th>Summa</th>
                            <th>Ränta</th>
                            <th>Utgångsdatum</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each $selectedItem.loans
                            .slice()
                            .sort((a, b) => new Date(a.expiration_date || 0) - new Date(b.expiration_date || 0)) as loan}
                            <tr>
                                <td class="bank-column"
                                    >{loan.bank_name
                                        ? toTitleCase(loan.bank_name)
                                        : "Uppgifter saknas"}</td
                                >
                                <td>{displayInt(loan.amount)} kr</td>
                                <td
                                    >{loan?.rate_percentage != null
                                        ? loan?.rate_percentage?.toLocaleString(
                                              "sv-SE",
                                              {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                              },
                                          ) + " %"
                                        : "Uppgifter saknas"}</td
                                >
                                <td
                                    >{loan.expiration_date
                                        ? loan.expiration_date
                                        : "Uppgifter saknas"}</td
                                >
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        </div>
    {/if}
</div>

<style>
    /* Horizontal layout for sections */
    .sections-container {
        display: flex;
        gap: 24px;
        margin-bottom: 24px;
    }

    .section {
        flex: 1;
        min-width: 0; /* Prevents flex items from overflowing */
    }

    /* For the loans section which spans full width */
    .loans-detail-section {
        width: 100%;
    }

    .metrics-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        gap: 16px;
    }

    .metric-card {
        position: relative;
    }

    .warning-bottom-right {
        position: absolute;
        bottom: 30px;
        right: 16px;
    }

    .loan-table-container {
        overflow-x: auto;
    }

    .loan-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.9rem;
    }

    .loan-table th,
    .loan-table td {
        padding: 10px;
        text-align: left; /* Default alignment restored */
        border-bottom: 1px solid #e9ecef;
        vertical-align: middle;
    }

    .loan-table th {
        font-weight: 600;
        color: #495057;
        background-color: #f8f9fa;
    }

    .loan-table tr:last-child td {
        border-bottom: none;
    }

    /* Responsive adjustments */
    @media (max-width: 1024px) {
        .sections-container {
            flex-direction: column;
        }

        /* Hide bank column on smaller screens (e.g., less than 768px) */
        .loan-table .bank-column {
            display: none;
        }
    }

    /* Ensure bank column is visible on larger screens (override the potential mobile-first hiding) */
    @media (min-width: 768px) {
        /* Adjust breakpoint as needed */
        .loan-table .bank-column {
            display: table-cell; /* Or appropriate display type */
        }
    }
</style>
